import { useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../api/client";
import { keys, useInvalidate } from "../api/hooks";
import { paySettingsSchema, type PaySettings } from "../../api/contracts";
import type { PaySnapshot, Verdict } from "../../results/types";
import { Banner, Button, ErrorNote, Field, Input, Panel, Stat, cx } from "./ui";
import { LineChart } from "./charts";
import { formatDay } from "../lib/format";
import { deltaTone, pctChange, signedPct, verdictBanner } from "../lib/results";
import { paySavings, paySummary, staffRows, wholeMoney, weeklySeries, windowFigures } from "../lib/pay";

const TEXT_TONE = { success: "text-hs-success" } as const;
const BANNER_TONE = { success: "success", neutral: "info" } as const;
const BELOW_WAGE = "Below this line you pay a top-up to reach minimum wage";
const FOOTNOTE = "Estimated from scheduled shifts and the commission table, not from payroll. The top-up is estimated per salon.";

const dollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const signedDollars = (cents: number) => `${cents < 0 ? "−" : "+"}${dollars(Math.abs(cents))}`;
const orDash = (cents: number | null) => (cents === null ? "—" : dollars(cents));

const WAGE_ERROR = "Enter the minimum wage in dollars, like 12.50 (between $1.00 and $50.00).";
const RATE_ERROR = "Enter the average commission rate as a percent, like 19 (between 5% and 40%).";

/** Empty = not set (null); a plain decimal inside [min, max] units, else undefined. */
function toUnits(text: string, pattern: RegExp, min: number, max: number): number | null | undefined {
  const t = text.trim();
  if (t === "") return null;
  if (!pattern.test(t)) return undefined;
  const n = Math.round(Number(t.replace(/^\$\s*/, "")) * 100);
  return n >= min && n <= max ? n : undefined;
}

/** Coloured only when it moved the way that's good for the owner (`good`); never shown as a warning. */
function Delta({ pct, good }: { pct: number | null; good?: "up" | "down" }) {
  const tone = good && pct !== null ? deltaTone(good === "up" ? pct : -pct) : "neutral";
  return (
    <span className="block">
      <span className={cx("mr-1 font-medium", tone === "success" && TEXT_TONE.success)}>{pct === null ? "—" : signedPct(pct)}</span>vs before automatic BTB
    </span>
  );
}

function Settings({ blvdId, settings }: { blvdId: string; settings: PaySettings }) {
  const invalidate = useInvalidate();
  const [wage, setWage] = useState(settings.minWageCents === null ? "" : (settings.minWageCents / 100).toFixed(2));
  const [rate, setRate] = useState(settings.fallbackCommissionBp === null ? "" : String(settings.fallbackCommissionBp / 100));
  const [invalid, setInvalid] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: PaySettings) => api(`/api/results/${blvdId}/pay-settings`, paySettingsSchema, { method: "PUT", body }),
    onSuccess: () => invalidate(keys.results),
  });
  const submit = () => {
    const minWageCents = toUnits(wage, /^\$?\s*\d+(\.\d{1,2})?$/, 100, 5000);
    const fallbackCommissionBp = toUnits(rate, /^\d+(\.\d{1,2})?$/, 500, 4000);
    if (minWageCents === undefined) return setInvalid(WAGE_ERROR);
    if (fallbackCommissionBp === undefined) return setInvalid(RATE_ERROR);
    setInvalid(null);
    save.mutate({ minWageCents, fallbackCommissionBp });
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start gap-3">
        <Field label="Minimum wage ($/hr)" className="w-44"><Input inputMode="decimal" value={wage} onChange={(e) => setWage(e.target.value)} /></Field>
        <Field label="Average commission rate (%)" hint="Used only for estheticians without a tier on record; applies from the next nightly refresh." className="w-72">
          <Input inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} />
        </Field>
        <div className="pt-5"><Button disabled={save.isPending} onClick={submit}>Save</Button></div>
      </div>
      {invalid && <Banner tone="danger">{invalid}</Banner>}
      {!invalid && save.isError && <ErrorNote error={save.error} />}
    </div>
  );
}

export function PayPanel({ blvdId, goLive, verdict, pay, settings, admin }: {
  blvdId: string; goLive: string; verdict: Verdict; pay: PaySnapshot | null | undefined; settings: PaySettings; admin: boolean;
}) {
  const wage = settings.minWageCents;
  const ready = !!pay && pay.since.window.days > 0 && pay.since.months.some((m) => m.minutes > 0);
  let body: ReactNode;
  if (!pay || !ready) {
    body = <p className="text-sm text-hs-text-muted">Pay numbers aren't available yet.</p>;
  } else {
    const since = windowFigures(pay.since, wage), before = windowFigures(pay.before, wage);
    // Like the rest of the page, no comparison until there are two full weeks.
    const early = verdict.state === "too_early";
    const summary = early ? null : paySummary(pay.before, pay.since, wage);
    const series = weeklySeries(pay);
    // The before weeks, the gap slot, then the since weeks.
    const gap = pay.before.weeks.length;
    const bands = [{ from: 0, to: gap - 1, label: "Before automatic BTB" }, { from: gap + 1, to: series.x.length - 1, label: "With BTB" }].filter((b) => b.to >= b.from);
    const rows = staffRows(pay, wage);
    const head = "px-3 py-2 text-left text-xs font-medium text-hs-text-muted";
    const change = (a: number | null, b: number | null) => (a === null || b === null ? null : pctChange(a, b));
    const delta = (pct: number | null, good?: "up" | "down") => (early ? undefined : <Delta pct={pct} good={good} />);
    // The savings tile only when the summary leads with savings, so the two never disagree.
    const savings = summary?.tone === "success" ? paySavings(pay.before, pay.since, wage) : null;
    const saving = savings !== null && savings.perHourCents > 0 && savings.perMonthCents > 0 ? savings : null;
    body = (
      <div className="space-y-4">
        {early && <Banner tone="info">{verdictBanner(verdict, goLive).text}</Banner>}
        {summary && <Banner tone={BANNER_TONE[summary.tone]}>{summary.text}</Banner>}
        <div className={cx("grid gap-4", saving ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3")}>
          {saving && (
            <Stat label="You're saving on esthetician pay" value={`${wholeMoney(saving.perMonthCents)} / month`}
              detail={`${dollars(saving.perHourCents)} less per hour on shift than before automatic BTB`} />
          )}
          <Stat label="Commission per hour on shift" value={orDash(since.commissionPerHourCents)}
            detail={delta(change(since.commissionPerHourCents, before.commissionPerHourCents))} />
          {wage !== null && (
            <>
              <Stat label="Your cost per hour" value={orDash(since.costPerHourCents)}
                detail={delta(change(since.costPerHourCents, before.costPerHourCents), "down")} />
              <Stat label="Minimum-wage top-up" value={since.topUpPerMonthCents === null ? "—" : `${dollars(since.topUpPerMonthCents)} / month`}
                detail={delta(change(since.topUpPerMonthCents, before.topUpPerMonthCents), "down")} />
            </>
          )}
        </div>
        {wage === null && <p className="text-sm text-hs-text-muted">Enter your minimum wage to see your cost per hour and top-up.</p>}
        <LineChart title="What your estheticians earned per hour on shift" x={series.x} xTick={(x) => (/^\d{4}-\d{2}-\d{2}$/.test(x) ? formatDay(x) : x)}
          format={(v) => `$${v.toFixed(2)}`} series={[{ label: "Average commission per hour on shift", values: series.values }]}
          bands={bands} references={wage === null ? undefined : [{ value: wage / 100, label: `Your minimum wage (${dollars(wage)}/hr)`, below: BELOW_WAGE }]} />
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-hs-surface-alt">
              <tr>
                <th scope="col" className={head}>Esthetician</th>
                <th scope="col" className={cx(head, "text-right")}>Hours</th>
                <th scope="col" className={cx(head, "text-right")}>Commission per hour</th>
                <th scope="col" className={cx(head, "text-right")}>Change vs before</th>
                {wage !== null && <th scope="col" className={cx(head, "text-right")}>Top-up (recent weeks)</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.staffId} className="border-t border-hs-border">
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{(r.minutes / 60).toFixed(1)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{orDash(r.commissionPerHourCents)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{early || r.changeCents === null ? "—" : signedDollars(r.changeCents)}</td>
                  {wage !== null && <td className="px-3 py-2 text-right tabular-nums">{orDash(r.topUpCents)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {admin && pay.salesCents > 0 && <p className="text-xs text-hs-text-muted">Unclassified sales: {Math.round((pay.unclassifiedCents / pay.salesCents) * 100)}% of counted sales</p>}
        <p className="text-xs text-hs-text-muted">{FOOTNOTE}</p>
      </div>
    );
  }
  return (
    <Panel title="Esthetician pay">
      <div className="space-y-4">
        {body}
        <Settings key={`${settings.minWageCents}:${settings.fallbackCommissionBp}`} blvdId={blvdId} settings={settings} />
      </div>
    </Panel>
  );
}
